import { createElement, CSSProperties, useState } from 'react';
import { MessagePopoverProps } from './types';
import { postfixTestId } from '../../../../util/testId';
import { Text } from '../../../Text';
import { renderInlineCssVars, useDesignTokens, DesignTokens } from '../../../../theme';
import { Popover } from '../../../Popover';
import { usePreviousValue } from '../../../../hooks/usePreviousValue';
import cn from './MessagePopover.css.json';
import './MessagePopover.css';

// as per UX, the default message width should be this
const defaultMessageWidth = 160;

export const calculatePopoverWidth = (
  assignedWidth: string | undefined,
  inputContainerWidth: number,
  messageContainerWidth: number | undefined
) => {
  if (!assignedWidth) {
    assignedWidth = Math.max(
      Math.min(inputContainerWidth, messageContainerWidth!),
      defaultMessageWidth
    ).toString();
  }

  return assignedWidth;
};

const DisplayMessage = (props: MessagePopoverProps) => {
  const dt = useDesignTokens();

  const tokens = {
    ...renderInlineCssVars(getThemeTokens(dt, props.status), 'message-popover'),
  };

  const [messageWidth, setMessageWidth] = useState<CSSProperties['width'] | undefined>();

  const previousStatus = usePreviousValue(props.status);
  const isStatusChanged = props.status !== previousStatus;

  const previousMessage = usePreviousValue(props.message);
  const isMessageChanged = props.message !== previousMessage;

  return (
    <Popover
      referenceElementRef={props.referenceElementRef}
      hideArrow
      className={cn.content}
      placement="bottom-start"
      focusLockProps={{ disabled: true }}
      style={{
        ...tokens,
        width: isStatusChanged || isMessageChanged ? undefined : messageWidth,
        maxWidth: props.maxWidth,
        minWidth: props.minWidth,
      }}
      testId={props.testId}
      ref={(popover) => {
        if (popover && popover.container && (!messageWidth || isMessageChanged)) {
          setMessageWidth(
            `${calculatePopoverWidth(
              props.width?.toString(),
              parseFloat(
                props.referenceElementRef?.current?.getBoundingClientRect().width.toString()!
              ),
              parseFloat(popover.container.getBoundingClientRect().width.toString()!)
            )}px`
          );
        }
      }}
    >
      <Text testId={postfixTestId(props.testId, 'messageContainer')}>{props.message}</Text>
    </Popover>
  );
};

export const MessagePopover = (props: MessagePopoverProps) => {
  return !props.hidden && props.message ? <DisplayMessage {...props} /> : null;
};

const getThemeTokens = (dt: DesignTokens, status: MessagePopoverProps['status']) => {
  if (dt.enableNovaTokens) {
    return {
      bd: dt.themeVariant === 'high-contrast' ? `1px solid ${dt.c.colorUiBorder}` : 'none',
      bg: dt.stateStatusPadBoxNova[status].bg,
      fg: dt.stateStatusPadBoxNova[status].fg,
      'pd-squish-s': dt.spacing.insetSquish.s.padding,
      'font-size-xs': dt.fontNova.size.xs,
    };
  } else {
    // legacy tokens
    return {
      bd: `1px solid ${dt.stateStatus[status].bd}`,
      bg: dt.stateStatus[status].bg,
      fg: dt.stateStatus[status].fg,
      'pd-squish-s': dt.spacing.insetSquish.s.padding,
      'font-size-xs': dt.font.size.xs,
    };
  }
};
